/**
 * NeedlesBoard - tonight's top NBA/NHL prop needles and their graded record.
 *
 * The nightly top 5 from runners/find_prop_needles.py (best needle per game),
 * each graded the next day against the player's actual stat
 * (GET /api/parlays/needles/board).
 */
import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const STAT_LABEL = { shots: 'shots', points: 'points', rebounds: 'rebounds', assists: 'assists' };

function shortDay(d) {
  return new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function NeedleCard({ n }) {
  const under = n.side === 'UNDER';
  const accent = under ? 'border-l-sky-500' : 'border-l-green-500';
  const sideColor = under ? 'text-sky-400' : 'text-green-400';
  const onSide = v => (under ? v < n.sb_line : v > n.sb_line);
  return (
    <div className={`glass-card p-4 border-l-4 ${accent}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-lg font-bold text-text-primary truncate">{n.player_name}</h3>
          <p className="text-xs text-text-secondary truncate">
            {n.sport.toUpperCase()} · {n.player_team} · {n.matchup}
          </p>
        </div>
        <div className="text-right shrink-0">
          <div className="text-2xl font-extrabold text-text-primary leading-none">{Math.round(n.backtest_pct)}%</div>
          <div className="text-[10px] text-text-secondary mt-1">rule's past<br />hit rate</div>
        </div>
      </div>
      <p className={`mt-2 text-sm font-bold uppercase tracking-wide ${sideColor}`}>
        {n.side} {n.sb_line} {STAT_LABEL[n.stat_type] || n.stat_type}
      </p>
      {n.last10.length > 0 && (
        <div className="mt-2 flex items-center gap-1 flex-wrap">
          <span className="text-[10px] font-semibold text-text-secondary mr-1">LAST {n.last10.length}</span>
          {n.last10.map((v, i) => (
            <span
              key={i}
              className={`w-7 h-6 flex items-center justify-center rounded text-xs font-bold ${
                onSide(v) ? (under ? 'bg-sky-500 text-bg-primary' : 'bg-green-500 text-bg-primary')
                          : 'bg-bg-secondary text-text-primary'
              }`}
            >
              {v}
            </span>
          ))}
        </div>
      )}
      {n.rule && <p className="mt-2 text-[11px] text-text-secondary">{n.rule.split(' | ')[0]}</p>}
    </div>
  );
}

function ResultRow({ n }) {
  const pending = n.hit === null;
  return (
    <div className="flex items-center gap-3 py-2">
      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
        pending ? 'bg-bg-secondary text-text-secondary' : n.hit ? 'bg-green-500 text-bg-primary' : 'bg-red-500 text-white'
      }`}>
        {pending ? '…' : n.hit ? '✓' : '✕'}
      </span>
      <span className="flex-1 text-sm text-text-primary truncate">
        {n.player_name}{' '}
        <span className="text-text-secondary">
          · {n.side.toLowerCase()} {n.sb_line} {STAT_LABEL[n.stat_type] || n.stat_type}
        </span>
      </span>
      <span className={`text-sm font-bold ${pending ? 'text-text-secondary' : n.hit ? 'text-green-400' : 'text-red-400'}`}>
        {n.actual === null ? 'pending' : Math.round(n.actual)}
      </span>
    </div>
  );
}

export default function NeedlesBoard() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get('/parlays/needles/board?days=30')
      .then(res => setData(res.data))
      .catch(err => {
        console.error('Error fetching needles board:', err);
        setError(true);
      });
  }, []);

  if (error) {
    return <div className="glass-card p-6 text-center text-text-secondary">Couldn't load tonight's needles.</div>;
  }
  if (!data) {
    return <div className="glass-card p-6 animate-pulse h-48" />;
  }

  const day = new Date(`${data.game_date}T12:00:00`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const losses = data.graded - data.hits;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-4">
        <div>
          <h2 className="text-2xl font-extrabold text-text-primary tracking-tight">Tonight's Top Needles</h2>
          <p className="text-sm text-text-secondary">{day} · NBA &amp; NHL props where our model and recent games agree</p>
        </div>
        {data.graded > 0 && (
          <span className="self-start sm:self-auto px-3 py-1 rounded-full bg-accent-blue/15 text-accent-blue text-sm font-semibold">
            Record {data.hits}-{losses} ({Math.round((100 * data.hits) / data.graded)}%)
          </span>
        )}
      </div>

      {data.tonight.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.tonight.map(n => <NeedleCard key={`${n.player_id}-${n.stat_type}`} n={n} />)}
        </div>
      ) : (
        <div className="glass-card p-6 text-center text-text-secondary">
          Tonight's needles post once the evening lines are in.
        </div>
      )}

      {data.nights.length > 0 && (
        <div className="glass-card p-4 mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-text-secondary mb-2">Graded</h3>
          {data.nights.map(night => (
            <div key={night.game_date} className="mb-3 last:mb-0">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-text-primary">{shortDay(night.game_date)}</span>
                {night.graded > 0 && (
                  <span className={`text-sm font-extrabold ${night.hits === night.graded ? 'text-green-400' : 'text-text-primary'}`}>
                    {night.hits} for {night.graded}
                  </span>
                )}
              </div>
              <div className="divide-y divide-white/5">
                {night.legs.map(n => <ResultRow key={`${n.player_id}-${n.stat_type}`} n={n} />)}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-text-secondary mt-3">
        Past hit rate = how often the rule that picked this prop was right in our backtest. The record above is
        tonight's board's real results since Oct 28. For entertainment. 21+. Gamble responsibly.
      </p>
    </div>
  );
}
